import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cloneMasterBudgetSchema, CloneMasterBudgetInput } from "./masterBudgetSchema";
import { useCloneMasterBudget, MasterBudget } from "@/hooks/useMasterBudgets";

const currentYear = new Date().getFullYear();
// Offer the next 10 strictly-future years
const FUTURE_YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => currentYear + 1 + i);

interface CloneMasterBudgetDialogProps {
  sourceBudget: MasterBudget;
  trigger?: React.ReactNode;
}

export function CloneMasterBudgetDialog({ sourceBudget, trigger }: CloneMasterBudgetDialogProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useCloneMasterBudget();

  const form = useForm<CloneMasterBudgetInput>({
    resolver: zodResolver(cloneMasterBudgetSchema),
    defaultValues: {
      name: sourceBudget.name,
      description: sourceBudget.description ?? "",
      targetYear: currentYear + 1,
    },
  });

  // Keep form in sync if the source budget changes while dialog is closed
  useEffect(() => {
    if (!open) {
      form.reset({
        name: sourceBudget.name,
        description: sourceBudget.description ?? "",
        targetYear: currentYear + 1,
      });
    }
  }, [open, sourceBudget, form]);

  const onSubmit = async (data: CloneMasterBudgetInput) => {
    const newBudget = await mutateAsync({
      sourceId: sourceBudget.id,
      ...data,
    });
    if (!newBudget) return;
    setOpen(false);
    navigate(`/budgets/master/${newBudget.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? <Button variant="outline">Clone Budget</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Clone Master Budget</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Cloning <span className="font-medium text-foreground">{sourceBudget.name}</span> ({sourceBudget.year})
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Annual Budget 2026" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="targetYear"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target Year</FormLabel>
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(parseInt(v, 10))}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select target year" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {FUTURE_YEAR_OPTIONS.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Brief description of this budget"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Cloning..." : "Clone"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
